import Analytics from '@web/services/Analytics/Analytics'
import { trackBannerClick } from '@web/services/Analytics/v2/banners'

/// События для главной страницы

/**
 * Событие открытия главной страницы
 * @param userId - ID пользователя
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'main_open', user_id, event_date
 */
export function analyticsEventMainOpen (userId: string, typelk = 'influence_lk'): void {
  Analytics.send({
    action: 'main_open',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'При загрузке главной страницы',
    target: 'Отслеживание входов',
    type_lk: typelk
  })
}

/**
 * Событие показа баннера на главной странице
 * @param userId - ID пользователя
 * @param bannerId - ID баннера
 * @param blockSlot - слот баннера (mobile_mainPage_top и т.п.)
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'banner_view', user_id, banner_id, block_slot, event_date
 */
export function analyticsEventBannerView ({ userId, bannerId, blockSlot, promo, typelk = 'influence_lk' }: { userId: string, bannerId: string, blockSlot: string, promo: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'banner_view',
    user_id: userId,
    banner_id: bannerId,
    block_slot: blockSlot,
    event_date: new Date().toISOString(),
    when_trigger: 'Когда показывается баннер',
    target: 'Отслеживание показов баннеров',
    utm_source: 'lk',
    utm_medium: 'banner',
    // Разметка ссылок остаётся на слоте — это не параметр аналитики
    utm_campaign: blockSlot,
    utm_term: bannerId,
    promo,
    type_lk: typelk
  })
}

/**
 * Событие клика по баннеру на главной странице
 * @param userId - ID пользователя
 * @param bannerId - ID баннера
 * @param blockSlot - слот баннера (mobile_mainPage_top и т.п.)
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'banner_click', user_id, banner_id, block_slot, event_date
 */
export function analyticsEventBannerClick ({ userId, bannerId, blockSlot, promo, typelk = 'influence_lk' }: { userId: string, bannerId: string, blockSlot: string, promo: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'banner_click',
    user_id: userId,
    banner_id: bannerId,
    block_slot: blockSlot,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по баннеру',
    target: 'Переходы по баннерам',
    utm_source: 'lk',
    utm_medium: 'banner',
    // Разметка ссылок остаётся на слоте — это не параметр аналитики
    utm_campaign: blockSlot,
    utm_term: bannerId,
    promo,
    type_lk: typelk
  })
  trackBannerClick({ bannerId, oldPosition: blockSlot })
}

/**
 * Событие клика по кнопке баннера на главной странице
 * @param userId - ID пользователя
 * @param bannerId - ID баннера
 * @param blockSlot - слот баннера (mobile_mainPage_top и т.п.)
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'banner_button_click', user_id, banner_id, block_slot, event_date
 */
export function analyticsEventBannerButtonClick ({ userId, bannerId, blockSlot, typelk = 'influence_lk' }: { userId: string, bannerId: string, blockSlot: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'banner_button_click',
    user_id: userId,
    banner_id: bannerId,
    block_slot: blockSlot,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по кнопке на баннере с кнопкой',
    target: 'Клики по кнопке на баннерах',
    utm_source: 'lk',
    utm_medium: 'banner_button',
    // Разметка ссылок остаётся на слоте — это не параметр аналитики
    utm_campaign: blockSlot,
    utm_term: bannerId,
    type_lk: typelk
  })
}

/**
 * Событие клика по категории из блока "Больше категорий"
 * @param userId - ID пользователя
 * @param categoryName - название категории
 * @param position - порядковый номер чипа в списке категорий, 1-based, по факту рендера
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'category_click', user_id, category_name, position, event_date
 */
export function analyticsEventCategoryClick ({ userId, categoryName, position, typelk = 'influence_lk' }: { userId: string, categoryName: string, position?: number, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'category_click',
    user_id: userId,
    category_name: categoryName,
    ...(position ? { position: String(position) } : {}),
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по тегу из блока “Больше категорий”',
    target: 'Интересы пользователя',
    type_lk: typelk
  })
}

/**
 * Событие просмотра карточки курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param categoryName - название категории (идентифицирует ряд)
 * @param position - место карточки внутри своего ряда, 1-based, по факту рендера
 * @param rowPosition - номер ряда сверху вниз, 1-based, по факту рендера
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'course_card_view', user_id, course_page_id, category_name, position, row_position, event_date
 */
export function analyticsEventCourseCardView ({ userId, coursePageId, categoryName, position, rowPosition, typelk = 'influence_lk' }: { userId: string, coursePageId: string, categoryName: string, position?: number, rowPosition?: number, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'course_card_view',
    user_id: userId,
    course_page_id: coursePageId,
    category_name: categoryName,
    // Вне ряда (поиск) позиции нет — ключи не отправляем вовсе
    ...(position ? { position: String(position) } : {}),
    ...(rowPosition ? { row_position: String(rowPosition) } : {}),
    event_date: new Date().toISOString(),
    when_trigger: 'Видимость карточек определенной категории',
    target: 'Вовлеченность в контент',
    type_lk: typelk
  })
}

/**
 * Событие клика по карточке курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param categoryName - название категории (идентифицирует ряд)
 * @param position - место карточки внутри своего ряда, 1-based, по факту рендера
 * @param rowPosition - номер ряда сверху вниз, 1-based, по факту рендера
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'course_card_click', user_id, course_page_id, category_name, position, row_position, event_date
 */
export function analyticsEventCourseCardClick ({ userId, coursePageId, categoryName, position, rowPosition, typelk = 'influence_lk' }: { userId: string, coursePageId: string, categoryName: string, position?: number, rowPosition?: number, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'course_card_click',
    user_id: userId,
    course_page_id: coursePageId,
    category_name: categoryName,
    ...(position ? { position: String(position) } : {}),
    ...(rowPosition ? { row_position: String(rowPosition) } : {}),
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по карточке курса',
    target: 'Интересы пользователя',
    type_lk: typelk
  })
}

/**
 * Событие глубины прокрутки главной страницы
 * @param userId - ID пользователя
 * @param scrollPercent - процент прокрутки ('25', '50', '75', '100')
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'scroll_depth', user_id, scroll_percent, event_date
 */
export function analyticsEventScrollDepth ({ userId, scrollPercent, typelk = 'influence_lk' }: { userId: string, scrollPercent: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'scroll_depth',
    user_id: userId,
    scroll_percent: scrollPercent,
    event_date: new Date().toISOString(),
    when_trigger: 'Пользователь проскроллил страницу (25/50/75/100%)',
    target: 'Измерение вовлечённости',
    type_lk: typelk
  })
}

/// События для страницы курса

/** Что видит пользователь при открытии лендинга курса (три сценария эпика UNIFIED-COURSE-LANDING) */
export type CourseLandingPageState = 'no_subscription' | 'not_added' | 'added'

/**
 * Событие открытия промо-лендинга курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param pageState - что видит пользователь: 'no_subscription' (продающие блоки, нет СП),
 *                    'not_added' (есть СП, курс можно добавить), 'added' (курс уже в «Мои курсы»)
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'course_page_open', user_id, course_page_id, page_state, event_date
 */
export function analyticsEventCoursePageOpen ({ userId, coursePageId, pageState, typelk = 'influence_lk' }: { userId: string, coursePageId: string, pageState?: CourseLandingPageState, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'course_page_open',
    user_id: userId,
    course_page_id: coursePageId,
    ...(pageState ? { page_state: pageState } : {}),
    event_date: new Date().toISOString(),
    when_trigger: 'При открытии промо лендинга курса (когда кликают на карточку курса с главной)',
    target: 'Построение воронки',
    type_lk: typelk
  })
}

/**
 * Событие клика по баннеру "Попробовать бесплатно"
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'banner_cta_try_free_click', user_id, course_page_id, event_date
 */
export function analyticsEventBannerCtaTryFreeClick ({ userId, coursePageId, typelk = 'influence_lk' }: { userId: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'banner_cta_try_free_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по баннеру "Попробовать бесплатно"',
    target: 'переходы по баннерам',
    type_lk: typelk
  })
}

/**
 * Событие клика по закрепленной кнопке "Попробовать бесплатно"
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'fix_cta_try_free_click', user_id, course_page_id, event_date
 */
export function analyticsEventFixCtaTryFreeClick ({ userId, coursePageId, typelk = 'influence_lk' }: { userId: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'fix_cta_try_free_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик на закрепе по кнопке "попробовать бесплатно"',
    target: 'Переход в пэйвол',
    type_lk: typelk
  })
}

/**
 * Событие клика по зафиксированной кнопке внизу страницы в мобильной версии
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'fix_mobile_cta_try_free_click', user_id, course_page_id, event_date
 */
export function analyticsEventFixMobileCtaTryFreeClick ({ userId, coursePageId, typelk = 'influence_lk' }: { userId: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'fix_mobile_cta_try_free_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по зафиксированной кнопке внизу страницы в мобильной версии',
    target: 'Переход в пэйвол',
    type_lk: typelk
  })
}

/**
 * Событие клика по закрепленной кнопке "Получить доступ"
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'fix_cta_buy_course_click', user_id, course_page_id, event_date
 */
export function analyticsEventFixCtaBuyCourseClick ({ userId, coursePageId, typelk = 'influence_lk' }: { userId: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'fix_cta_buy_course_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик на закрепе по кнопке "получить доступ"',
    target: 'Интерес к покупке курса',
    type_lk: typelk
  })
}

/**
 * Событие клика по кнопке "Назад" на странице курса
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'back_to_main_click', user_id, course_page_id, event_date
 */
export function analyticsEventBackToMainClick ({ userId, coursePageId, typelk = 'influence_lk' }: { userId: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'back_to_main_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по кнопке назад',
    target: 'Навигация',
    type_lk: typelk
  })
}

/// События для пэйволла

/**
 * Событие прокрутки пэйволла
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param scrollPercent - процент прокрутки
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'paywall_scroll_depth', user_id, course_page_id, scroll_percent, event_date
 */
export function analyticsEventPaywallScrollDepth ({ userId, coursePageId, scrollPercent, typelk = 'influence_lk' }: { userId: string, scrollPercent: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'paywall_scroll_depth',
    user_id: userId,
    course_page_id: coursePageId,
    scroll_percent: scrollPercent,
    event_date: new Date().toISOString(),
    when_trigger: 'Прокрутка пэйвола',
    target: 'Насколько изучают экран',
    type_lk: typelk
  })
}

/**
 * Событие клика по кнопке "Начать пробный период" на пэйволле
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'paywall_cta_click', user_id, course_page_id, event_date
 */
export function analyticsEventPaywallCtaClick ({ userId, coursePageId, typelk = 'influence_lk' }: { userId: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'paywall_cta_click',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик “Начать пробный период”',
    target: 'Конверсия в переход на форму оплаты',
    type_lk: typelk
  })
}

/**
 * Событие закрытия пэйволла
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'paywall_close', user_id, course_page_id, event_date
 */
export function analyticsEventPaywallClose ({ userId, coursePageId, typelk = 'influence_lk' }: { userId: string, coursePageId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'paywall_close',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик на крестик закрытия пэйвола',
    target: 'Навигация',
    type_lk: typelk
  })
}

/// События для модальных окон

/**
 * Событие показа модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'modal_view', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalView ({ userId, coursePageId = '', modalName = 'quiz', typelk = 'influence_lk' }: { userId: string, coursePageId?: string, modalName?: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'modal_view',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Показ модального окна',
    target: 'Отслеживание количества показов',
    type_lk: typelk
  })
}

/**
 * Событие клика по модальному окну
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'modal_click', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalClick ({ userId, coursePageId = '', modalName = 'quiz', typelk = 'influence_lk' }: { userId: string, coursePageId?: string, modalName?: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'modal_click',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по нему',
    target: 'Конверсия в переход',
    type_lk: typelk
  })
}

/**
 * Событие закрытия модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'modal_close', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalClose ({ userId, coursePageId = '', modalName = 'quiz', typelk = 'influence_lk' }: { userId: string, coursePageId?: string, modalName?: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'modal_close',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Закрытие окна',
    target: 'Конверсия в закрытие',
    type_lk: typelk
  })
}

/**
 * Событие закрытия модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'modal_qwiz_close', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalQwizClose ({ userId, coursePageId = '', modalName = 'quiz', typelk = 'influence_lk' }: { userId: string, coursePageId?: string, modalName?: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'modal_qwiz_close',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Закрытие окна',
    target: 'Конверсия в закрытие',
    type_lk: typelk
  })
}

/**
 * Событие выбора "Больше не показывать" для модального окна
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param modalName - название модального окна
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'modal_dont_ask_again', user_id, course_page_id, modal_name, event_date
 */
export function analyticsEventModalNeverShow ({ userId, coursePageId = '', modalName = 'quiz', typelk = 'influence_lk' }: { userId: string, coursePageId?: string, modalName?: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'modal_never_show',
    user_id: userId,
    course_page_id: coursePageId,
    modal_name: modalName,
    event_date: new Date().toISOString(),
    when_trigger: 'Выбор "Больше не показывать"',
    target: 'Отслеживание отказов от показа',
    type_lk: typelk
  })
}

/// События для коротких курсов

/**
 * Событие нажатия на кнопку "Добавить в мои курсы"
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'add_course_SS', user_id, course_id, course_name, event_date
 */
export function analyticsEventAddCourseSS ({ userId, courseId, courseName, typelk = 'influence_lk' }: { userId: string, courseId: string, courseName: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'add_course_SS',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Нажатие на "Добавить в мои курсы"',
    target: 'Отслеживание добавления курсов',
    type_lk: typelk
  })
}

/**
 * Событие успешного добавления курса
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'course_added_SS', user_id, course_id, course_name, event_date
 */
export function analyticsEventCourseAddedSS ({ userId, courseId, courseName, typelk = 'influence_lk' }: { userId: string, courseId: string, courseName: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'course_added_SS',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Когда курс был добавлен',
    target: 'Подтверждение добавления курса',
    type_lk: typelk
  })
}

/**
 * Событие нажатия на кнопку "Перейти в курс"
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'add_course_SS_go', user_id, course_id, course_name, event_date
 */
export function analyticsEventAddCourseSSGo ({ userId, courseId, courseName, typelk = 'influence_lk' }: { userId: string, courseId: string, courseName: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'add_course_SS_go',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Нажатие на кнопку "Перейти в курс"',
    target: 'Переход в курс',
    type_lk: typelk
  })
}

/**
 * Событие клика по карточке своего курса на главном экране
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param placeButton - откуда клик: главный экран или страница «все курсы»
 * @param position - место карточки внутри своего ряда, 1-based, по факту рендера
 * @param rowPosition - номер ряда сверху вниз, 1-based, по факту рендера
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'main_screen_my_courses', user_id, course_id, course_name, place_button, position, row_position, event_date
 */
export function analyticsMainScreenMyCourses ({
  userId,
  courseId,
  courseName,
  placeButton = 'main_screen',
  position,
  rowPosition,
  typelk = 'influence_lk'
}: {
  userId: string,
  courseId: string;
  courseName: string;
  placeButton: 'main_screen' | 'show_all',
  position?: number,
  rowPosition?: number,
  typelk?: AnalyticsTypeLk
}): void {
  Analytics.send({
    action: 'main_screen_my_courses',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    place_button: placeButton,
    // Вне ряда (страница «все курсы») позиции нет — ключи не отправляем вовсе
    ...(position ? { position: String(position) } : {}),
    ...(rowPosition ? { row_position: String(rowPosition) } : {}),
    event_date: new Date().toISOString(),
    when_trigger: 'клик по карточке курса или кнопке, ведущей на курс с главного экрана ЛК',
    target: 'Переход в курс',
    type_lk: typelk
  })
}

/**
 * Событие показа карточки своего курса на главном экране
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param placeButton - откуда показ: главный экран или страница «все курсы»
 * @param position - место карточки внутри своего ряда, 1-based, по факту рендера
 * @param rowPosition - номер ряда сверху вниз, 1-based, по факту рендера
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'main_screen_my_courses_view', user_id, course_id, course_name, place_button, position, row_position, event_date
 */
export function analyticsMainScreenMyCoursesView ({
  userId,
  courseId,
  courseName,
  placeButton = 'main_screen',
  position,
  rowPosition,
  typelk = 'influence_lk'
}: {
  userId: string,
  courseId: string;
  courseName: string;
  placeButton: 'main_screen' | 'show_all',
  position?: number,
  rowPosition?: number,
  typelk?: AnalyticsTypeLk
}): void {
  Analytics.send({
    action: 'main_screen_my_courses_view',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    place_button: placeButton,
    ...(position ? { position: String(position) } : {}),
    ...(rowPosition ? { row_position: String(rowPosition) } : {}),
    event_date: new Date().toISOString(),
    when_trigger: 'Видимость карточки своего курса на главном экране',
    target: 'Вовлеченность в контент',
    type_lk: typelk
  })
}

/**
 * Событие клика по кнопке перехода в клуб с главного экрана
 * @param userId - ID пользователя
 * @param clubId - ID клуба
 * @param clubName - название клуба
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'main_screen_club_button', user_id, club_id, club_name, event_date
 */
export function analyticsEventClubButtonClick ({ userId, clubId, clubName, typelk = 'influence_lk' }: { userId: string, clubId: string, clubName: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'main_screen_club_button',
    user_id: userId,
    club_id: clubId,
    club_name: clubName,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по кнопке/ссылке перехода в клуб с главного экрана',
    target: 'Переход в клуб',
    type_lk: typelk
  })
}

/**
 * Событие показа страницы домашки
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'homework_screen', user_id, course_id, course_name, event_date
 */
export function analyticsEventShowHomeworkPage ({ userId, courseId, courseName, typelk = 'influence_lk' }: { userId: string, courseId: string, courseName: string, typelk?: AnalyticsTypeLk }): void {
  const params = {
    action: 'homework_screen',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Показ страницы домашки',
    target: 'Показ страницы домашки',
    type_lk: typelk,
    ...(courseId && { course_id: courseId }),
    ...(courseName && { course_name: courseName })
  }
  Analytics.send(params)
}

/**
 * Событие нажатия на кнопку "Перейти в курс"
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param homeworkId - ID домашки
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'choose_homework', user_id, course_id, course_name, homework_id, event_date
 */
export function analyticsEventChooseHomework ({ userId, courseId, courseName, homeworkId, typelk = 'influence_lk' }: { userId: string, courseId: string, courseName: string, homeworkId: string, typelk?: AnalyticsTypeLk }): void {
  const params = {
    action: 'choose_homework',
    user_id: userId,
    homework_id: homeworkId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по домашке',
    target: 'Клик по домашке',
    type_lk: typelk,
    ...(courseId && { course_id: courseId }),
    ...(courseName && { course_name: courseName })
  }
  Analytics.send(params)
}

/**
 * Событие отправки домашки
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param homeworkId - ID домашки
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'homework_done', user_id, course_id, course_name, homework_id, event_date
 */
export function analyticsEventSendHomework ({ userId, courseId, courseName, homeworkId, typelk = 'influence_lk' }: { userId: string, courseId: string, courseName: string, homeworkId: string, typelk?: AnalyticsTypeLk }): void {
  const params = {
    action: 'homework_done',
    user_id: userId,
    homework_id: homeworkId,
    event_date: new Date().toISOString(),
    when_trigger: 'Отправка домашки',
    target: 'Отправка домашки',
    type_lk: typelk,
    ...(courseId && { course_id: courseId }),
    ...(courseName && { course_name: courseName })
  }
  Analytics.send(params)
}

/**
 * Событие клика по пункту меню "Видеоконсультации"
 * @param userId - ID пользователя
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'burger_video', user_id, event_date
 */
export function analyticsEventVideoconsultationsMenuItemClick ({ userId, typelk = 'influence_lk' }: { userId: string, typelk?: AnalyticsTypeLk }): void {
  const params = {
    action: 'burger_video',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по пункту меню "Видеоконсультации"',
    target: 'Клик по пункту меню "Видеоконсультации"',
    place_button: 'burger',
    type_lk: typelk
  }
  Analytics.send(params)
}

/**
 * Событие клика по кнопке "Купить консультацию"
 * @param userId - ID пользователя
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'buy_video_consultation', user_id, event_date
 */
export function analyticsEventBuyVideoConsultation ({ userId, typelk = 'influence_lk', courseId, purchaseId }: { userId: string, typelk?: AnalyticsTypeLk, courseId: string, purchaseId: string }): void {
  Analytics.send({
    action: 'buy_video_consultation',
    user_id: userId,
    course_id: courseId,
    purchase_id: purchaseId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по кнопке "Купить консультацию"',
    target: 'Клик по кнопке "Купить консультацию"',
    type_lk: typelk
  })
}

/**
 * Событие клика по кнопке "Поддержка"
 * @param userId - ID пользователя
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'support_button', user_id, event_date
 */
export function analyticsEventCareServiceButtonClick ({ userId, typelk = 'influence_lk' }: { userId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'support_button',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по пункту меню "Поддержка"',
    target: 'Клик по пункту меню "Поддержка"',
    type_lk: typelk
  })
}

/**
 * Событие клика по пункту меню "Подписка"
 * @param userId - ID пользователя
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'menu_subscription', user_id, event_date
 */
export function analyticsEventMenuSubscription ({ userId, typelk = 'influence_lk' }: { userId: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'menu_subscription',
    user_id: userId,
    event_date: new Date().toISOString(),
    when_trigger: 'Клик по пункту меню "Подписка"',
    target: 'Клик по пункту меню "Подписка"',
    place_button: 'burger',
    type_lk: typelk
  })
}

/**
 * Событие открытия экрана курса
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'course_menu', user_id, course_id, course_name, event_date
 */
export function analyticsEventOpenCoursePage ({ userId, typelk = 'influence_lk', courseId, courseName }: { userId: string, typelk?: AnalyticsTypeLk, courseId: string, courseName: string }): void {
  Analytics.send({
    action: 'course_menu',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    event_date: new Date().toISOString(),
    when_trigger: 'Открытие экрана курса',
    target: 'Открытие экрана курса',
    type_lk: typelk
  })
}

/**
 * Событие первого нажатия на день в конкретном курсе
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'first_day_tap', user_id, course_id, event_date
 */
export function analyticsEventFirstTimeDayOpen ({ userId, typelk = 'influence_lk', courseId }: { userId: string, typelk?: AnalyticsTypeLk, courseId: string }): void {
  Analytics.send({
    action: 'first_day_tap',
    user_id: userId,
    course_id: courseId,
    event_date: new Date().toISOString(),
    when_trigger: 'Первое в жизни пользователя нажатие на любой день в конкретном курсе',
    target: 'Первое в жизни пользователя нажатие на любой день в конкретном курсе',
    type_lk: typelk
  })
}

/**
 * Событие нажатия на день в конкретном курсе
 * @param userId - ID пользователя
 * @param courseId - ID курса
 * @param courseName - название курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'day_tap', user_id, course_id, event_date
 */
export function analyticsEventDayTap ({ userId, typelk = 'influence_lk', courseId, courseName, dayNumber, dayLessonId }: { userId: string, typelk?: AnalyticsTypeLk, courseId: string, courseName: string, dayNumber: string, dayLessonId: string }): void {
  Analytics.send({
    action: 'day_tap',
    user_id: userId,
    course_id: courseId,
    course_name: courseName,
    day_number: dayNumber,
    day_lesson_id: dayLessonId,
    event_date: new Date().toISOString(),
    when_trigger: 'Нажатие на день в конкретном курсе',
    target: 'Нажатие на день в конкретном курсе',
    type_lk: typelk
  })
}

/// События исхода one-click-оплаты

/** Причина неуспеха оплаты; no_payment_url — списание даже не стартовало */
export type OneClickPayFailReason = 'error' | 'fail' | 'timeout' | 'no_payment_url'

/**
 * Событие успешной one-click-оплаты
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса; 'home' для баннеров и модалок,
 *                       где платят за подписку, а не за конкретный курс
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'one_click_pay_success', user_id, course_page_id, event_date
 */
export function analyticsEventOneClickPaySuccess ({ userId, coursePageId = 'home', typelk = 'influence_lk' }: { userId: string, coursePageId?: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'one_click_pay_success',
    user_id: userId,
    course_page_id: coursePageId,
    event_date: new Date().toISOString(),
    when_trigger: 'Списание по привязанной карте подтвердилось',
    target: 'Конверсия в оплату',
    type_lk: typelk
  })
}

/**
 * Событие неуспешной one-click-оплаты
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса; 'home' для баннеров и модалок
 * @param reason - причина: 'error' (бэк отдал отказ сразу), 'fail' (инвойс
 *                 завершился отказом), 'timeout' (кончились попытки опроса),
 *                 'no_payment_url' (оплата вообще не началась — в позиции оффера
 *                 нет ссылок, кнопка на клик не отвечает)
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'one_click_pay_fail', user_id, course_page_id, reason, event_date
 */
export function analyticsEventOneClickPayFail ({ userId, coursePageId = 'home', reason, typelk = 'influence_lk' }: { userId: string, coursePageId?: string, reason: OneClickPayFailReason, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'one_click_pay_fail',
    user_id: userId,
    course_page_id: coursePageId,
    reason,
    event_date: new Date().toISOString(),
    when_trigger: 'Списание по привязанной карте не прошло',
    target: 'Потери на оплате',
    type_lk: typelk
  })
}

/**
 * Событие перехода на форму оплаты
 *
 * Шлётся прямо перед уходом со страницы на форму — когда one-click недоступен
 * (в позиции оффера нет oneClickUrl) и платить можно только через форму.
 * Отделяет «клик не сработал» от «переход был»: между кликом по CTA и
 * следующим course_page_open иначе пустота, и эти случаи неразличимы.
 *
 * @param userId - ID пользователя
 * @param coursePageId - ID страницы курса
 * @param position - позиция оффера: '1' — подписка с триалом, '3' — покупка курса
 * @param typelk - платформа (influence_lk, standart_lk)
 * Отправляет: action: 'payment_form_open', user_id, course_page_id, position, event_date
 */
export function analyticsEventPaymentFormOpen ({ userId, coursePageId, position, typelk = 'influence_lk' }: { userId: string, coursePageId: string, position: string, typelk?: AnalyticsTypeLk }): void {
  Analytics.send({
    action: 'payment_form_open',
    user_id: userId,
    course_page_id: coursePageId,
    position,
    event_date: new Date().toISOString(),
    when_trigger: 'Переход на форму оплаты (one-click недоступен)',
    target: 'Дошёл ли пользователь до формы оплаты',
    type_lk: typelk
  })
}
